import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  Modal,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconEye,
  IconKey,
  IconShieldLock,
} from '@tabler/icons-react';
import { merchantsApi } from '../api/merchants';
import { useProfile, usePermissions } from '../auth/SessionContext';
import { ApiCallError } from '../api/client';
import { formatGmt7 } from '../lib/format';
import { useTranslation } from '../i18n/LocaleContext';

type TokenReveal = { clientId: string; clientToken: string; webhookSecret?: string };

export function ApiCredentialsPage() {
  const { t } = useTranslation();
  const profile = useProfile();
  const { has } = usePermissions();
  const [tokenReveal, setTokenReveal] = useState<TokenReveal | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  // Password prompt for step-up auth before the server returns the plaintext.
  const [pwPromptOpen, setPwPromptOpen] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  // MFA step-up: revealing the token requires an MFA code when the account has
  // MFA enrolled. The field is optional in the form (accounts without MFA leave
  // it blank); the server enforces it and we surface mfa_required inline here.
  const [mfaInput, setMfaInput] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);

  if (profile.userType !== 'external' || profile.merchantId == null) {
    return (
      <Alert color="gray" icon={<IconAlertCircle size={16} />}>
        {t('apiCred.merchantOnly')}
      </Alert>
    );
  }
  const merchantId = profile.merchantId;
  // The "Lihat token" button is gated by the `account.credentials.reveal`
  // permission (not a hardcoded role). Superadmin grants it to whichever
  // merchant roles should be able to reveal the plaintext token; merchant_admin
  // holds it by default. Revealing is a sensitive, audited action with password
  // + MFA step-up, kept separate from the plain `account.credentials.view`.
  const canRevealToken = has('account.credentials.reveal');

  const merchantQ = useQuery({
    queryKey: ['merchant', merchantId],
    queryFn: () => merchantsApi.get(merchantId),
    retry: 0,
  });

  const revealMut = useMutation({
    meta: { successNotification: false, errorNotification: false }, // reveal shows the token in UI; not a "save"; errors handled inline
    mutationFn: (vars: { password: string; mfaCode: string }) =>
      merchantsApi.revealCredentials(merchantId, vars.password, vars.mfaCode || undefined),
    onSuccess: (data) => {
      setRevealError(null);
      setPwError(null);
      setMfaError(null);
      setPwPromptOpen(false);
      setPwInput('');
      setMfaInput('');
      setTokenReveal({
        clientId: data.transpayClientId,
        clientToken: data.transpayClientToken,
        webhookSecret: data.webhookSecret,
      });
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid_password') {
          // Show inline on the password modal so the user can retry.
          setPwError(t('apiCred.errInvalidPassword'));
          return;
        }
        if (err.code === 'mfa_required') {
          // Account has MFA enrolled — keep the modal open and prompt for the
          // MFA code instead of dead-ending with a raw server message.
          setMfaError(t('apiCred.errMfaRequired'));
          return;
        }
        if (err.code === 'invalid_mfa_code') {
          setMfaError(t('apiCred.errInvalidMfaCode'));
          return;
        }
        if (err.code === 'mfa_unavailable') {
          setPwPromptOpen(false);
          setRevealError(t('apiCred.errMfaUnavailable'));
          return;
        }
        if (err.code === 'token_not_revealable') {
          setPwPromptOpen(false);
          setRevealError(t('apiCred.errTokenNotRevealable'));
          return;
        }
        if (err.code === 'kms_decrypt_failed') {
          setPwPromptOpen(false);
          setRevealError(t('apiCred.errKmsDecrypt'));
          return;
        }
      }
      setPwPromptOpen(false);
      setRevealError((err as Error).message);
    },
  });

  const openPasswordPrompt = () => {
    setPwInput('');
    setPwError(null);
    setMfaInput('');
    setMfaError(null);
    setRevealError(null);
    setPwPromptOpen(true);
  };
  const submitPasswordPrompt = () => {
    if (!pwInput) {
      setPwError(t('apiCred.errPasswordRequired'));
      return;
    }
    setPwError(null);
    setMfaError(null);
    revealMut.mutate({ password: pwInput, mfaCode: mfaInput.trim() });
  };

  if (merchantQ.isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }
  if (merchantQ.error || !merchantQ.data) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {merchantQ.error instanceof ApiCallError
          ? `${merchantQ.error.code}: ${merchantQ.error.message}`
          : t('common.error')}
      </Alert>
    );
  }
  const m = merchantQ.data;

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>{t('apiCred.title')}</Title>
        <Text c="dimmed" size="sm">
          {t('apiCred.subtitle')}
        </Text>
      </div>

      <Card>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <Text fw={600}>{t('apiCred.activeCredentials')}</Text>
            <Group gap="xs">
              {canRevealToken && (
                <Button
                  variant="default"
                  leftSection={<IconEye size={14} />}
                  onClick={openPasswordPrompt}
                  loading={revealMut.isPending}
                  disabled={!m.transpayClientTokenSuffix}
                >
                  {t('apiCred.viewToken')}
                </Button>
              )}
            </Group>
          </Group>

          {revealError && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />} variant="light">
              {revealError}
            </Alert>
          )}

          <CredentialField
            label={t('apiCred.clientIdLabel')}
            value={m.transpayClientId}
            description={t('apiCred.clientIdDesc')}
          />

          <Stack gap={6}>
            <Text c="dimmed" size="xs" tt="uppercase" fw={700} lts={0.4}>
              {t('apiCred.clientTokenLabel')}
            </Text>
            <Group
              gap="xs"
              wrap="nowrap"
              style={{
                border: '1px solid var(--mantine-color-yellow-light)',
                background: 'var(--mantine-color-yellow-light-hover)',
                borderRadius: 6,
                padding: '10px 12px',
              }}
            >
              <Text
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  flex: 1,
                  wordBreak: 'break-all',
                  userSelect: 'none',
                  color: 'var(--mantine-color-dimmed)',
                }}
              >
                {'•'.repeat(40)}{m.transpayClientTokenSuffix ?? ''}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {canRevealToken
                ? t('apiCred.tokenHiddenAdmin')
                : t('apiCred.tokenHiddenNonAdmin')}
            </Text>
          </Stack>

          <Group gap="sm">
            <Text c="dimmed" size="xs" tt="uppercase" fw={600} w={110}>
              {t('apiCred.apiAccess')}
            </Text>
            <Badge color={m.apiAccessEnabled ? 'green' : 'gray'} variant="light">
              {m.apiAccessEnabled ? t('merchants.apiEnabled') : t('merchants.apiDisabled')}
            </Badge>
          </Group>
        </Stack>
      </Card>

      <Card>
        <Group gap="sm" mb="sm">
          <IconShieldLock size={18} />
          <Text fw={600}>{t('apiCred.usageHttp')}</Text>
        </Group>
        <Code block style={{ fontSize: 12, padding: 12 }}>
{`POST /api/v1/cashin HTTP/1.1
Host: api.trans-pay.app
Content-Type: application/json
X-Transpay-Client-Id: ${m.transpayClientId}
X-Transpay-Client-Token: <plaintext-token-shown-on-rotate>
Idempotency-Key: <uuidv7>

{ "amount": 50000, "referenceId": "REF-001" }`}
        </Code>
      </Card>

      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {t('apiCred.merchantUpdated', { at: formatGmt7(m.updatedAt) })}
        </Text>
      </Group>

      {/* Step-up: ask for password before calling the reveal endpoint. */}
      <Modal
        opened={pwPromptOpen}
        onClose={() => {
          if (!revealMut.isPending) setPwPromptOpen(false);
        }}
        title={
          <Group gap="sm">
            <IconShieldLock size={16} />
            <Text fw={600}>{t('apiCred.verifyPasswordTitle')}</Text>
          </Group>
        }
        size="md"
        closeOnClickOutside={!revealMut.isPending}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {t('apiCred.verifyPasswordIntro')}
          </Text>
          <PasswordInput
            label={t('apiCred.accountPasswordLabel')}
            placeholder="••••••••"
            value={pwInput}
            onChange={(e) => setPwInput(e.currentTarget.value)}
            error={pwError ?? undefined}
            data-autofocus
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitPasswordPrompt();
            }}
            disabled={revealMut.isPending}
          />
          <TextInput
            label={t('apiCred.mfaCodeLabel')}
            placeholder={t('apiCred.mfaCodePlaceholder')}
            description={t('apiCred.mfaCodeHint')}
            value={mfaInput}
            onChange={(e) => setMfaInput(e.currentTarget.value)}
            error={mfaError ?? undefined}
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={revealMut.isPending}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitPasswordPrompt();
            }}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setPwPromptOpen(false)}
              disabled={revealMut.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={submitPasswordPrompt} loading={revealMut.isPending}>
              {t('apiCred.viewToken')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={tokenReveal !== null}
        onClose={() => setTokenReveal(null)}
        title={
          <Group gap="sm">
            <IconKey size={16} />
            <Text fw={600}>{t('apiCred.tokenPlaintextTitle')}</Text>
          </Group>
        }
        size="lg"
        closeOnClickOutside={false}
      >
        {tokenReveal && (
          <Stack>
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              {t('apiCred.tokenRevealedWarning')}
            </Alert>
            <CredentialField label={t('apiCred.clientIdLabel')} value={tokenReveal.clientId} />
            <CredentialField
              label={t('apiCred.clientTokenLabel')}
              value={tokenReveal.clientToken}
              tone="warning"
            />
            {tokenReveal.webhookSecret && (
              <CredentialField
                label={t('apiCred.webhookSecretLabel')}
                value={tokenReveal.webhookSecret}
                description={t('apiCred.webhookSecretDesc')}
                tone="warning"
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setTokenReveal(null)}>
                {t('apiCred.close')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

/**
 * Block-style display for one credential: uppercase label, full-width
 * monospace value (selectable + with a copy button), optional helper text.
 * `tone="warning"` colors the value box yellow so the user notices it's a
 * sensitive field. Used in both the live card and the "one-time reveal" modal.
 */
function CredentialField({
  label,
  value,
  description,
  mono = true,
  tone = 'default',
}: {
  label: string;
  value: string;
  description?: string;
  mono?: boolean;
  tone?: 'default' | 'warning';
}) {
  const { t } = useTranslation();
  const borderColor =
    tone === 'warning'
      ? 'var(--mantine-color-yellow-light)'
      : 'var(--mantine-color-default-border)';
  const background =
    tone === 'warning'
      ? 'var(--mantine-color-yellow-light-hover)'
      : 'var(--mantine-color-default)';
  return (
    <Stack gap={6}>
      <Text c="dimmed" size="xs" tt="uppercase" fw={700} lts={0.4}>
        {label}
      </Text>
      <Group
        gap="xs"
        wrap="nowrap"
        style={{
          border: `1px solid ${borderColor}`,
          background,
          borderRadius: 6,
          padding: '10px 12px',
        }}
      >
        <Text
          style={{
            fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: 0.3,
            flex: 1,
            wordBreak: 'break-all',
            userSelect: 'all',
          }}
        >
          {value}
        </Text>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('apiCred.copied') : t('apiCred.copy')} withArrow>
              <ActionIcon
                size="lg"
                variant={copied ? 'filled' : 'default'}
                color={copied ? 'green' : undefined}
                onClick={copy}
                aria-label="Copy value"
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}
    </Stack>
  );
}
